/**
 * Carreira Offline (item 5.3), em `#carreira`: criar a carreira, a central da temporada (próxima partida,
 * classificação, resultados e seus números), jogar ou simular a partida da rodada e começar a temporada seguinte.
 * Tudo fica salvo no navegador depois de cada rodada.
 */
import { useMemo, useState } from "react";
import { BOT_LEVEL_PARAMS, POSITIONS, type Position } from "@besenliga/shared";
import { browserStore, loadRoster } from "../creator/roster";
import { pilotFrom } from "../flight/pilot";
import type { Clubs } from "../flight/view";
import { Game, Result, type MatchOutcome } from "../match/MatchScreen";
import {
  LEAGUE_LABEL,
  TEAMMATES,
  createCareer,
  currentRound,
  deleteCareer,
  finished,
  loadCareer,
  myFixture,
  mySeason,
  nextSeason,
  recordMyMatch,
  saveCareer,
  simulateMyMatch,
  table,
  youIndex,
  type Career,
  type LeagueLevel,
} from "./career";

const ROLES: Position[] = ["artilheiro", "batedor", "cacador", "goleiro"];
const hex = (c: number) => `#${c.toString(16).padStart(6, "0")}`;

type Step = "central" | "jogo" | "resultado";

export function CareerScreen() {
  const [career, setCareer] = useState<Career | null>(() => loadCareer(browserStore()));
  const [step, setStep] = useState<Step>("central");
  const [outcome, setOutcome] = useState<MatchOutcome | null>(null);
  const [round, setRound] = useState(0);
  const [saveFailed, setSaveFailed] = useState(false);

  const update = (c: Career | null) => {
    if (c) setSaveFailed(!saveCareer(browserStore(), c));
    else deleteCareer(browserStore());
    setCareer(c);
  };

  if (!career) return <NewCareer onCreate={update} />;

  const roster = loadRoster(browserStore());
  const saved = roster.find((a) => a.id === career.athleteId);
  const pilot = pilotFrom(saved, career.role);
  if (!saved) pilot.name = career.athleteName;
  const f = myFixture(career);
  const you = youIndex(career);
  const opp = f ? career.clubs[f.home === you ? f.away : f.home] : undefined;
  const mine = career.clubs[you];
  const clubs: Clubs | null =
    mine && opp ? { casa: { name: mine.name, kit: mine.kit }, fora: { name: opp.name, kit: opp.kit } } : null;
  const levels = opp ? { casa: TEAMMATES[career.league], fora: opp.level } : undefined;

  if (step === "jogo" && clubs && levels)
    return (
      <div className="match-screen">
        <Game
          key={round}
          pilot={pilot}
          opponents="medio"
          clubs={clubs}
          levels={levels}
          quitLabel="Voltar à carreira (a partida não conta)"
          onRestart={() => setRound((r) => r + 1)}
          onQuit={() => setStep("central")}
          onEnd={(o) => {
            setOutcome(o);
            setStep("resultado");
          }}
        />
      </div>
    );

  if (step === "resultado" && outcome && clubs)
    return (
      <Result pilot={pilot} results={outcome} clubs={clubs}>
        <button
          type="button"
          className="primary"
          onClick={() => {
            const me = outcome.lines.find((l) => l.you);
            update(
              recordMyMatch(career, {
                mine: outcome.hud.score.casa,
                theirs: outcome.hud.score.fora,
                endReason: outcome.hud.match.endReason ?? "tempo",
                line: me ? { role: me.role, rating: me.rating, stats: me.stats, played: true } : null,
              }),
            );
            setOutcome(null);
            setStep("central");
          }}
        >
          Continuar a carreira
        </button>
      </Result>
    );

  return (
    <Central
      career={career}
      saveFailed={saveFailed}
      onPlay={() => {
        setRound((r) => r + 1);
        setStep("jogo");
      }}
      onSimulate={() => update(simulateMyMatch(career))}
      onNextSeason={() => update(nextSeason(career))}
      onAbandon={() => update(null)}
    />
  );
}

function NewCareer({ onCreate }: { onCreate: (c: Career) => void }) {
  const roster = useMemo(() => loadRoster(browserStore()), []);
  const [athleteId, setAthleteId] = useState<string | null>(roster[0]?.id ?? null);
  const [role, setRole] = useState<Position>(roster[0]?.position ?? "artilheiro");
  const [league, setLeague] = useState<LeagueLevel>("normal");
  const athlete = roster.find((a) => a.id === athleteId);
  return (
    <main className="menu-screen">
      <section className="menu-card wide" aria-labelledby="carreira-nova">
        <header className="menu-head">
          <a href="#" className="ghost">
            Menu
          </a>
          <h1 id="carreira-nova">Nova carreira</h1>
        </header>
        <p>
          Uma temporada de pontos corridos contra 5 clubes, em turno e returno: 10 rodadas. Você joga a
          partida do seu clube; as outras são simuladas. Tudo fica salvo neste navegador.
        </p>
        <fieldset className="menu-group">
          <legend>Seu atleta</legend>
          {roster.length === 0 ? (
            <p>
              Você vai jogar com o atleta de teste. <a href="#criar">Criar seu atleta</a>
            </p>
          ) : (
            <div className="menu-options">
              {roster.map((a) => (
                <label key={a.id} className="menu-option">
                  <input
                    type="radio"
                    name="atleta"
                    checked={athleteId === a.id}
                    onChange={() => {
                      setAthleteId(a.id);
                      setRole(a.position);
                    }}
                  />
                  <span>
                    <strong>{a.name}</strong> · {POSITIONS[a.position].label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
        <fieldset className="menu-group">
          <legend>Posição</legend>
          <div className="segmented" role="group" aria-label="Posição">
            {ROLES.map((r) => (
              <button key={r} type="button" aria-pressed={role === r} onClick={() => setRole(r)}>
                {POSITIONS[r].label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset className="menu-group">
          <legend>Liga</legend>
          <div className="menu-options">
            {(
              [
                ["facil", "Adversários fáceis e médios; seus companheiros são médios."],
                ["normal", "Um fácil, três médios e um difícil; companheiros médios."],
                ["dificil", "Médios e difíceis; seus companheiros são difíceis."],
              ] as const
            ).map(([id, hint]) => (
              <label key={id} className="menu-option">
                <input type="radio" name="liga" checked={league === id} onChange={() => setLeague(id)} />
                <span>
                  <strong>{LEAGUE_LABEL[id]}</strong> · {hint}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="menu-actions row">
          <button
            type="button"
            className="primary"
            onClick={() =>
              onCreate(createCareer({ seed: Math.floor(Math.random() * 2 ** 31), athlete, role, league }))
            }
          >
            Começar a carreira
          </button>
        </div>
      </section>
    </main>
  );
}

function Central({
  career,
  saveFailed,
  onPlay,
  onSimulate,
  onNextSeason,
  onAbandon,
}: {
  career: Career;
  saveFailed: boolean;
  onPlay: () => void;
  onSimulate: () => void;
  onNextSeason: () => void;
  onAbandon: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const you = youIndex(career);
  const mine = career.clubs[you];
  const f = myFixture(career);
  const r = currentRound(career);
  const t = table(career);
  const me = mySeason(career);
  const done = finished(career);
  const pos = t.findIndex((x) => x.club === you) + 1;
  const name = (i: number) => career.clubs[i]?.name ?? "?";
  const opp = f ? career.clubs[f.home === you ? f.away : f.home] : undefined;
  const played = career.fixtures.filter((x) => x.played && (x.home === you || x.away === you));
  return (
    <main className="menu-screen">
      <section className="menu-card wide career" aria-labelledby="carreira-titulo">
        <header className="menu-head">
          <a href="#" className="ghost">
            Menu
          </a>
          <h1 id="carreira-titulo">
            {mine?.full} · Temporada {career.season}
          </h1>
        </header>
        <p className="career-sub">
          {career.athleteName} · {POSITIONS[career.role].label} · {LEAGUE_LABEL[career.league]}
          {saveFailed && <strong className="career-warn"> · Não foi possível salvar neste navegador</strong>}
        </p>

        {f && opp && r !== null && (
          <div className="career-next">
            <div>
              <p className="career-round">Rodada {r + 1} de 10</p>
              <h2>
                {name(f.home)} × {name(f.away)}
              </h2>
              <p>
                {f.home === you ? "Em casa" : "Fora"} · {opp.full} (bots{" "}
                {BOT_LEVEL_PARAMS[opp.level].label.toLowerCase()})
              </p>
            </div>
            <div className="menu-actions">
              <button type="button" className="primary" onClick={onPlay}>
                Jogar a partida
              </button>
              <button type="button" className="ghost" onClick={onSimulate}>
                Simular sem jogar
              </button>
            </div>
          </div>
        )}
        {done && (
          <div className="career-next done">
            <div>
              <p className="career-round">Temporada encerrada</p>
              <h2>Campeão: {career.clubs[t[0]?.club ?? 0]?.full}</h2>
              <p>
                {pos === 1 ? "É o seu clube! " : ""}Você terminou em {pos}º, com {t[pos - 1]?.points ?? 0}{" "}
                pontos.
              </p>
            </div>
            <div className="menu-actions">
              <button type="button" className="primary" onClick={onNextSeason}>
                Começar a temporada {career.season + 1}
              </button>
            </div>
          </div>
        )}

        <div className="career-cols">
          <table className="result-table career-table">
            <caption>Classificação</caption>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Clube</th>
                <th scope="col">J</th>
                <th scope="col">V</th>
                <th scope="col">E</th>
                <th scope="col">D</th>
                <th scope="col">Saldo</th>
                <th scope="col">Pts</th>
              </tr>
            </thead>
            <tbody>
              {t.map((row, i) => {
                const c = career.clubs[row.club];
                return (
                  <tr key={row.club} className={c?.you ? "you" : undefined}>
                    <td>{i + 1}</td>
                    <th scope="row">
                      <span className="career-dot" style={{ background: hex(c?.kit.primary ?? 0) }} />
                      {c?.name}
                    </th>
                    <td>{row.played}</td>
                    <td>{row.won}</td>
                    <td>{row.drawn}</td>
                    <td>{row.lost}</td>
                    <td>{row.scored - row.conceded}</td>
                    <td className="rating">{row.points}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div>
            <h3 className="career-h3">Sua temporada</h3>
            <p className="career-me">
              {me.played === 0
                ? "Você ainda não jogou nesta temporada."
                : `${me.played} partida${me.played > 1 ? "s" : ""} · nota média ${me.rating?.toFixed(1)} · ${me.gols} gols · ${me.passesCertos} passes certos · ${me.defesas} defesas · ${me.petardos} Petardos · ${me.capturas} Faíscas`}
            </p>
            <h3 className="career-h3">Seus jogos</h3>
            <ul className="career-results">
              {played.length === 0 && <li>Nenhum ainda.</li>}
              {played.map((x) => {
                const home = x.home === you;
                const ms = home ? x.homeScore : x.awayScore;
                const ts = home ? x.awayScore : x.homeScore;
                const res = ms > ts ? "V" : ms < ts ? "D" : "E";
                return (
                  <li key={x.round} data-result={res}>
                    <span className="career-badge">{res}</span> R{x.round + 1} · {name(x.home)} {x.homeScore}{" "}
                    × {x.awayScore} {name(x.away)}
                    {x.mine ? ` · sua nota ${x.mine.rating.toFixed(1)}` : " · simulada"}
                  </li>
                );
              })}
            </ul>
            {career.history.length > 0 && (
              <>
                <h3 className="career-h3">Temporadas anteriores</h3>
                <ul className="career-results">
                  {career.history.map((h) => (
                    <li key={h.season}>
                      Temporada {h.season}: campeão {h.champion}; você em {h.position}º ({h.points} pts)
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <div className="menu-actions row">
          {confirm ? (
            <>
              <span>Apagar esta carreira? Não dá para desfazer.</span>
              <button type="button" className="ghost" onClick={() => setConfirm(false)}>
                Não
              </button>
              <button type="button" className="ghost danger" onClick={onAbandon}>
                Apagar
              </button>
            </>
          ) : (
            <button type="button" className="ghost" onClick={() => setConfirm(true)}>
              Abandonar a carreira
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
